/**
 * StageTabNav — Folder divider style tab navigation
 * Rounded, overlapping tabs like physical binder dividers
 * Active tab is "pulled forward" with brighter background
 * Documents tab is always last with distinct styling
 * Traffic light dots: red (empty), orange (partial), green (complete)
 * Sprint 8 — S8-07, redesigned per CEO UI review
 */

import { useMemo } from 'react'
import type { TabDefinition } from '../../types'
import { getVisibleTabs, type TabStatus } from '../../lib/tabRegistry'

export interface StageTabNavProps {
  currentStage: string
  activeTabId: string
  onTabChange: (tabId: string) => void
  tabStatuses?: Record<string, TabStatus>
}

const STATUS_COLORS: Record<TabStatus, string> = {
  empty: '#EF4444',     // red
  partial: '#F59E0B',   // orange
  complete: '#22C55E',  // green
  none: 'transparent',
}

export function StageTabNav({
  currentStage,
  activeTabId,
  onTabChange,
  tabStatuses = {},
}: StageTabNavProps) {
  const visibleTabs = useMemo(() => getVisibleTabs(currentStage), [currentStage])

  return (
    <div
      className="relative overflow-x-auto scrollbar-hide"
      role="tablist"
      aria-label="Project lifecycle tabs"
    >
      {/* Bottom border line that tabs sit on */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-border-subtle" />

      <div className="flex items-end min-w-max px-2 pt-1">
        {visibleTabs.map((tab: TabDefinition, idx: number) => {
          const isActive = tab.id === activeTabId
          const isDocuments = tab.id === 'tab-documents'
          const status = tabStatuses[tab.id] || 'none'

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={`
                relative whitespace-nowrap transition-all duration-150 flex items-center gap-1.5
                ${isActive ? 'z-20' : `z-${10 - idx}`}
                ${idx > 0 ? '-ml-2' : ''}
              `}
              style={{
                borderRadius: '10px 10px 0 0',
                padding: isActive ? '8px 20px 7px' : '6px 18px 5px',
                fontSize: '13px',
                fontWeight: isActive ? 600 : 400,
                color: isActive
                  ? (isDocuments ? '#60A5FA' : '#e8e8f0')
                  : (isDocuments ? '#4a8acc' : '#8888a8'),
                backgroundColor: isActive ? '#1e1e36' : '#16162a',
                borderTop: isActive
                  ? (isDocuments ? '3px solid #3B82F6' : '2px solid #E61B25')
                  : (isDocuments ? '2px solid #3a4a60' : '1px solid #2a2a44'),
                borderLeft: isDocuments ? '2px solid #3a4a60' : '1px solid #2a2a44',
                borderRight: isDocuments ? '2px solid #3a4a60' : '1px solid #2a2a44',
                borderBottom: isActive ? '1px solid #1e1e36' : '1px solid #2a2a44',
                marginBottom: isActive ? '-1px' : '0',
                // Documents gets a small left gap to separate from content tabs
                marginLeft: isDocuments && idx > 0 ? '6px' : (idx > 0 ? '-8px' : '0'),
              }}
            >
              {/* Traffic light dot */}
              {status !== 'none' && (
                <span
                  className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                />
              )}
              {/* Documents icon */}
              {isDocuments && (
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              )}
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default StageTabNav
